if (!customElements.get('new-configurator')) {
  class NewConfigurator extends HTMLElement {
    constructor() {
      super();
      this.options = this.querySelectorAll('[data-option]');
      this.configuratorSidebar = document.querySelector('configurator-sidebar');
      this.configuratorSlider = this.configuratorSidebar.querySelector('configurator-slider');
      this.prices = document.querySelectorAll('[data-product-info] [data-final-price]');
      this.comparePrices = document.querySelectorAll('[data-product-info] [data-compare-price]');

      this.addButton = document.querySelector('[data-product-form-button]');

      this.setupOptionClickListeners();
      this.setupAddButtonListener();
    }

    setupOptionClickListeners() {
      this.options.forEach(option => {
        option.addEventListener('click', (event) => this.handleOptionClick(option, event));
      });
    }

    handleOptionClick(option, event) {
      event.stopPropagation();

      // Check if the click is on the remove button
      const removeButton = event.target.closest('[data-remove]');
      if (removeButton) {
        // If clicked on remove button, remove the option
        if (option.hasAttribute('data-checked')) {
          this.handleOptionUnselect(option);
        }
        return;
      }

      // If not clicked on remove button, open sidebar
      this.configuratorSidebar?.openSidebar();
    }

    handleOptionUnselect(option) {
      option.removeAttribute('data-checked');
      this.resetOptionImage(option);

      const optionIndex = Array.from(this.options).indexOf(option);
      if (this.configuratorSidebar) {
        this.removeProductFromSidebar(optionIndex);
      }
    }

    resetOptionImage(option) {
      const optionImage = option.querySelector('img');
      const fallbackImage = this.configuratorSidebar?.state.notSelectedImage;
      if (optionImage && fallbackImage) {
        optionImage.src = fallbackImage;
      }
    }

    removeProductFromSidebar(optionIndex) {
      const product = this.configuratorSidebar?.state?.optionsRender?.[optionIndex];
      this.configuratorSidebar.removeProductFromState(optionIndex);
      if (product) {
        this.configuratorSidebar.syncProductCardAfterRemoval(product.id);
      }
      this.configuratorSidebar.renderProductsToOptions();
    }

    ensureOptionImage(option) {
      return option.querySelector('img');
    }

    resetOptionElement(option, fallbackImage) {
      option.removeAttribute('data-checked');
      option.removeAttribute('data-border');
      const optionImage = option.querySelector('img');
      if (optionImage && fallbackImage) {
        optionImage.src = fallbackImage;
      }
    }

    renderOptionElement(option, product, fallbackImage) {
      if (!product) return;
      const image = this.ensureOptionImage(option);

      if (!image) return;

      if (product.image) {
        image.src = product.image;
        image.alt = product.title || '';
        image.removeAttribute('srcset');
      } else if (fallbackImage) {
        image.src = fallbackImage;
      }

      option.setAttribute('data-checked', '');
    }

    renderOptionList(options, renderList, fallbackImage) {
      if (!options) return;
      options.forEach((option) => this.resetOptionElement(option, fallbackImage));
      renderList.forEach((product, index) => {
        if (index < options.length) {
          this.renderOptionElement(options[index], product, fallbackImage);
        }
      });
    }

    updateOptions(state) {
      if (!this.options || !state) return;
      const renderList = state.optionsRender || state.products || [];

      this.renderOptionList(this.options, renderList, state.notSelectedImage);
      const safeActiveOption = this.calculateSafeActiveOption(state);
      this.updateOptionBorders(safeActiveOption);
      this.updateSelectedBowlsDisplay(state);
    }

    calculateSafeActiveOption(state) {
      const maxByQuantity = Number.isFinite(state?.maxQuantity)
        ? Math.max(0, state.maxQuantity - 1)
        : this.options.length - 1;
      const hardMax = Math.max(0, Math.min(maxByQuantity, this.options.length - 1));
      return Math.min(
        Math.max(0, Number.isFinite(state?.activeOption) ? state.activeOption : 0),
        hardMax
      );
    }

    updateOptionBorders(activeOption) {
      if (!this.options) return;

      const safeActive = this.calculateSafeActiveIndex(activeOption);
      this.clearAllOptionBorders();
      this.setBorderForActiveOption(safeActive);
    }

    calculateSafeActiveIndex(activeOption) {
      return Math.min(
        Math.max(0, Number.isFinite(activeOption) ? activeOption : 0),
        this.options.length - 1
      );
    }

    setBorderForActiveOption(safeActive) {
      if (safeActive >= 0 && safeActive < this.options.length) {
        const isLastOption = safeActive === this.options.length - 1;
        const isChecked = this.options[safeActive].hasAttribute('data-checked');

        // Don't set border if it's the last option and it's checked
        if (isLastOption && isChecked) {
          return;
        }

        this.options[safeActive].setAttribute('data-border', '');
      }
    }

    clearAllOptionBorders() {
      if (!this.options) return;
      this.options.forEach((option) => {
        option.removeAttribute('data-border');
      });
    }

    updateSelectedBowlsDisplay(state) {
      const selectedBowlsElement = this.querySelector('[data-selected-bowls]');
      const selectedTotalElement = this.querySelector('[data-selected-total]');

      if (!selectedBowlsElement || !selectedTotalElement || !state) return;

      // Calculate filled count from state
      const bowlsFilled = (state.bowlProducts || []).filter(Boolean).length;
      const optionalFilled = (state.optionalProducts || []).length;
      const filledCount = bowlsFilled + optionalFilled;
      const bowlsQuantity = state.bowlsQuantity || 0;

      // Show only the count (numerator) in data-selected-bowls
      selectedBowlsElement.textContent = filledCount;

      const totalQuantity = filledCount <= bowlsQuantity ? bowlsQuantity : filledCount;
      const format =
        (selectedTotalElement.dataset.quantityTextFormat || "").trim() ||
        "von %quantity% ausgewählt";
      selectedTotalElement.textContent = format.replaceAll(
        "%quantity%",
        String(totalQuantity)
      );
    }

    formatMoney(price) {
      return "€" + (price / 100).toFixed(2).replace(".", ",").replace(",00", "");
    }

    getActiveOptionPrice(state) {
      // Get price from currentVariant in state
      const currentVariant = state?.currentVariant;

      if (currentVariant) {
        return {
          price: currentVariant.price || 0,
          compareAtPrice: currentVariant.compareAtPrice || 0
        };
      }

      // Fallback: When currentVariant is not available, get price from first variant (first option element)
      if (this.options && this.options.length > 0) {
        const firstOption = this.options[0];
        const price = firstOption.getAttribute('data-price');
        const compareAtPrice = firstOption.getAttribute('data-compare-at-price');

        return {
          price: price ? parseInt(price) : 0,
          compareAtPrice: compareAtPrice ? parseInt(compareAtPrice) : 0
        };
      }
      return { price: 0, compareAtPrice: 0 };
    }

    updatePrices(state) {
      if (!this.prices || !this.comparePrices) return;

      const { price, compareAtPrice } = this.getActiveOptionPrice(state);

      // Update regular prices
      this.prices.forEach(priceElement => {
        priceElement.textContent = this.formatMoney(price);
        priceElement.setAttribute('data-final-price', String(price));
      });

      // Check if compare_at_price exists and is greater than price
      const hasComparePrice = compareAtPrice && compareAtPrice > price;

      this.comparePrices.forEach(comparePriceElement => {
        if (hasComparePrice) {
          comparePriceElement.textContent = this.formatMoney(compareAtPrice);
          comparePriceElement.style.display = '';
        } else {
          comparePriceElement.style.display = 'none';
        }
      });

      document.dispatchEvent(
        new CustomEvent('new-configurator:price-updated', {
          detail: { price, compareAtPrice }
        })
      );
    }

    setupAddButtonListener() {
      if (!this.addButton || !this.configuratorSidebar) return;

      // If cross-selling exists, let cross-selling handle the click / cart add
      const crossSelling = document.querySelector('cross-selling');
      if (crossSelling) return;

      this.addButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.configuratorSidebar.addToDrawer();
      });
    }

  }
  customElements.define('new-configurator', NewConfigurator);
}

if (!customElements.get('configurator-sidebar')) {
  class ConfiguratorSidebar extends HTMLElement {
    constructor() {
      super();
      this.newConfigurator = document.querySelector('new-configurator');
      this.configuratorSlider = this.querySelector('configurator-slider');
      this.closeButton = this.querySelector('[data-close]');
      this.filters = this.querySelectorAll('[data-filter]');
      this.tabs = this.querySelectorAll('[data-tab]');
      this.aside = this.querySelector('[data-aside]');
      this.backdrop = this.querySelector('[data-backdrop]');
      this.products = this.querySelectorAll('[data-product]');
      this.options = this.querySelectorAll('[data-option]');

      this.prices = document.querySelectorAll('[data-product-info] [data-final-price]');
      this.comparePrices = document.querySelectorAll('[data-product-info] [data-compare-price]');

      this.sidebarButton = this.querySelector('[data-sidebar-button]');
      this.cartDrawer = document.querySelector('cart-drawer');

      this.initialState = {
        activeOption: 0,
        maxQuantity: parseInt(this.getAttribute('data-max-quantity')),
        bowlsQuantity: parseInt(this.getAttribute('data-bowls-quantity')),
        optionalQuantity: parseInt(this.getAttribute('data-optional-quantity')),
        activeFilter: "all",
        notSelectedImage: this.getAttribute('data-not-selected-image'),
        bowlProducts: [],
        optionalProducts: [], // compact list
        products: [],
        optionsRender: [],
        currentVariant: {
          id: null,
          price: 0,
          compareAtPrice: 0,
          title: null
        },
        allVariants: this.querySelector('[data-all-variants]') ? JSON.parse(this.querySelector('[data-all-variants]').textContent) : [],

      };
      this.initialState.bowlProducts = new Array(this.initialState.bowlsQuantity || 0).fill(null); // fixed slots with holes

      this.state = { ...this.initialState };
      this.recomputeDerivedState();
    }

    connectedCallback() {
      this.setupListeners();
      this.updateOptionBorders();

      // Initialize new-configurator options
      if (this.newConfigurator) {
        this.newConfigurator.updateOptions(this.state);
      }

      // Initialize selected bowls display
      this.updateSelectedBowlsDisplay();

      // Initialize product quantities based on current state
      this.initializeProductQuantities();

      // Initialize button states
      this.updateButtonStates();
    }

    initializeProductQuantities() {
      this.products.forEach((product) => {
        const productId = product.dataset.id || product.dataset.sku;
        if (productId) {
          this.updateProductQuantity(product, productId);
        }
      });
    }

    getOptionTypeFromProduct(product) {
      // Explicit data-option wins; otherwise infer by current active slot
      if (product?.dataset?.option) return product.dataset.option;
      return this.state.activeOption >= this.state.bowlsQuantity ? 'optional' : 'bowl';
    }

    isBowlOption(optionIndex) {
      return Number.isInteger(optionIndex) && optionIndex < this.state.bowlsQuantity;
    }

    getFilledCount() {
      const bowlsFilled = this.state.bowlProducts.filter(Boolean).length;
      return bowlsFilled + this.state.optionalProducts.length;
    }

    getNextActiveOptionIndex() {
      const bowlHole = this.state.bowlProducts.findIndex((p) => !p);
      if (bowlHole !== -1) return bowlHole;

      const optionalIndex = this.state.optionalProducts.length;
      const optionalStart = this.state.bowlsQuantity;
      const candidate = optionalStart + optionalIndex;
      return Math.min(candidate, this.options.length - 1);
    }

    buildOptionsRenderList() {
      const renderList = new Array(this.options.length).fill(null);

      // Bowls keep their holes
      this.state.bowlProducts.forEach((product, idx) => {
        if (idx < renderList.length && product) {
          // Set variant ID from the option element at this index
          const optionElement = this.options?.[idx];
          const variantId = optionElement?.dataset?.variantId || product.variantId || null;
          renderList[idx] = {
            ...product,
            variantId: variantId
          };
        }
      });

      // Optional compacts
      const optionalStart = this.state.bowlsQuantity;
      this.state.optionalProducts.forEach((product, idx) => {
        const target = optionalStart + idx;
        if (target < renderList.length && product) {
          // Set variant ID from the option element at this index
          const optionElement = this.options?.[target];
          const variantId = optionElement?.dataset?.variantId || product.variantId || null;
          renderList[target] = {
            ...product,
            variantId: variantId
          };
        }
      });

      return renderList;
    }

    recomputeDerivedState() {
      this.state.optionsRender = this.buildOptionsRenderList();
      this.state.products = [
        ...this.state.bowlProducts.filter(Boolean),
        ...this.state.optionalProducts,
      ];

      // Set currentVariant based on allVariants if available
      const optionsRender = this.state.optionsRender || [];
      const allVariants = this.state.allVariants || [];

      if (allVariants.length > 0) {
        // Count total non-null items in optionsRender (min 4 and more)
        const totalCount = Math.max(4, optionsRender.filter(item => item !== null && item !== undefined).length);

        // Count items with additionalPrice: true (strictly boolean true)
        // Only count items where additionalPrice is explicitly true, not truthy values
        const additionalPriceCount = optionsRender.filter(
          item => {
            if (item === null || item === undefined) return false;
            // Explicitly check for boolean true, not truthy values
            return item.additionalPrice === true;
          }
        ).length;

        // Match variant by title pattern: "{totalCount} Dosen ({additionalPriceCount})"
        const expectedTitle = `${totalCount} Dosen (${additionalPriceCount})`;
        const matchedVariant = allVariants.find(variant => variant.title === expectedTitle);

        if (matchedVariant) {
          this.state.currentVariant = {
            id: matchedVariant.id ? String(matchedVariant.id) : null,
            price: matchedVariant.price ? parseInt(matchedVariant.price) : 0,
            compareAtPrice: matchedVariant.compare_at_price ? parseInt(matchedVariant.compare_at_price) : 0,
            title: matchedVariant.title || null
          };
        } else {
          // Fallback to last product if no variant matched
          let lastProduct = null;
          for (let i = optionsRender.length - 1; i >= 0; i--) {
            if (optionsRender[i] !== null && optionsRender[i] !== undefined) {
              lastProduct = optionsRender[i];
              break;
            }
          }

          if (lastProduct) {
            // Get variant title from allVariants by variantId
            const variantId = lastProduct.variantId ? String(lastProduct.variantId) : null;
            const variant = variantId ? allVariants.find(v => String(v.id) === variantId) : null;
            const variantTitle = variant?.title || null;

            this.state.currentVariant = {
              id: variantId,
              price: lastProduct.price ? parseInt(lastProduct.price) : 0,
              compareAtPrice: lastProduct.compareAtPrice ? parseInt(lastProduct.compareAtPrice) : 0,
              title: variantTitle
            };
          } else {
            // When optionsRender is empty, get variant info from first option element
            if (this.options && this.options.length > 0) {
              const firstOption = this.options[0];
              const variantId = firstOption.dataset.variantId || null;
              // Get variant title from allVariants by variantId
              const variant = variantId ? allVariants.find(v => String(v.id) === variantId) : null;
              const variantTitle = variant?.title || null;

              this.state.currentVariant = {
                id: variantId,
                price: firstOption.getAttribute('data-price') ? parseInt(firstOption.getAttribute('data-price')) : 0,
                compareAtPrice: firstOption.getAttribute('data-compare-at-price') ? parseInt(firstOption.getAttribute('data-compare-at-price')) : 0,
                title: variantTitle
              };
            } else {
              this.state.currentVariant = {
                id: null,
                price: 0,
                compareAtPrice: 0,
                title: null
              };
            }
          }
        }
      } else {
        // Original logic when allVariants is empty
        // When allVariants is empty, we can't get variant title, so set to null
        let lastProduct = null;
        for (let i = optionsRender.length - 1; i >= 0; i--) {
          if (optionsRender[i] !== null && optionsRender[i] !== undefined) {
            lastProduct = optionsRender[i];
            break;
          }
        }

        if (lastProduct) {
          this.state.currentVariant = {
            id: lastProduct.variantId || null,
            price: lastProduct.price ? parseInt(lastProduct.price) : 0,
            compareAtPrice: lastProduct.compareAtPrice ? parseInt(lastProduct.compareAtPrice) : 0,
            title: null // Can't get variant title when allVariants is empty
          };
        } else {
          // When optionsRender is empty, get variant info from first option element
          if (this.options && this.options.length > 0) {
            const firstOption = this.options[0];
            this.state.currentVariant = {
              id: firstOption.dataset.variantId || null,
              price: firstOption.getAttribute('data-price') ? parseInt(firstOption.getAttribute('data-price')) : 0,
              compareAtPrice: firstOption.getAttribute('data-compare-at-price') ? parseInt(firstOption.getAttribute('data-compare-at-price')) : 0,
              title: null // Can't get variant title when allVariants is empty
            };
          } else {
            this.state.currentVariant = {
              id: null,
              price: 0,
              compareAtPrice: 0,
              title: null
            };
          }
        }
      }

      const nextIndex = this.getNextActiveOptionIndex();
      this.state.activeOption = this.clampActiveOption(nextIndex);
      this.updateButtonStates();
    }

    clampActiveOption(value = 0) {
      const optionsLength = this.options?.length || 0;
      const maxByQuantity = Number.isFinite(this.state?.maxQuantity)
        ? Math.max(0, this.state.maxQuantity - 1)
        : optionsLength - 1;
      const hardMax = Math.max(0, Math.min(maxByQuantity, optionsLength - 1));
      const safeValue = Number.isFinite(value) ? value : 0;
      return Math.min(Math.max(0, safeValue), hardMax);
    }

    setupListeners() {
      this.setupCloseButtonListener();
      this.setupAsideClickListener();
      this.setupBackdropClickListener();
      this.setupOutsideClickListener();
      this.setupFilterListeners();
      this.setupProductListeners();
      this.setupOptionRemovalListeners();
      this.setupSidebarButtonListener();
    }

    setupCloseButtonListener() {
      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => {
          this.closeSidebar();
        });
      }
    }

    setupAsideClickListener() {
      if (this.aside) {
        this.aside.addEventListener('click', (event) => {
          event.stopPropagation();
        });
      }
    }

    setupBackdropClickListener() {
      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => {
          if (this.hasAttribute('data-open')) {
            this.closeSidebar();
          }
        });
      }
    }

    setupOutsideClickListener() {
      document.addEventListener('click', this.handleOutsideClick.bind(this));
    }

    setupFilterListeners() {
      this.filters.forEach((filter) => {
        filter.addEventListener('click', () => {
          const filterValue = filter.getAttribute('data-filter');
          this.setActiveFilter(filter);
          this.updateTabs(filterValue);
        });
      });
    }

    setupProductListeners() {
      this.products.forEach((product) => {
        product.addEventListener('click', (e) => {
          const minusButton = e.target.closest('[data-minus]');
          if (minusButton) {
            e.stopPropagation();
            this.handleMinusClick(product);
            this.configuratorSlider?.reinitialize();
            return;
          }
          this.handlePlusClick(product);
          this.configuratorSlider?.reinitialize();
          console.log(this.state);
        });
      });
    }

    setupOptionRemovalListeners() {
      this.options.forEach((option) => {
        option.addEventListener('click', (event) => this.handleOptionClick(option, event));
      });
    }

    handleOptionClick(option, event) {
      event.stopPropagation();
      if (!this.isOptionChecked(option)) return;

      this.clearOptionVisualState(option);

      const optionIndex = Array.from(this.options).indexOf(option);
      const product = this.state?.optionsRender?.[optionIndex];
      if (product) {
        this.removeOptionProductFromState(optionIndex);
      }

      this.renderProductsToOptions();
      this.configuratorSlider?.reinitialize();
      console.log(this.state);
    }

    removeOptionProductFromState(optionIndex) {
      const product = this.state?.optionsRender?.[optionIndex];
      this.removeProductFromState(optionIndex);
      if (product) {
        this.syncProductCardAfterRemoval(product.id);
      }
    }

    syncProductCardAfterRemoval(productId) {
      const productElement = Array.from(this.products).find((p) => p.dataset.id === productId);
      if (!productElement) return;

      this.updateProductQuantity(productElement, productId);
      if (this.hasProductInState(productId)) return;

      this.resetProductCardVisualState(productElement);
    }

    resetProductCardVisualState(productElement) {
      productElement.removeAttribute('data-checked');
      this.updateProductImageToFallback(productElement);
    }

    updateProductImageToFallback(productElement) {
      const productImage = productElement.querySelector('.new-configurator__image');
      if (productImage && this.state.notSelectedImage) {
        productImage.src = this.state.notSelectedImage;
      }
    }

    clearOptionVisualState(option) {
      option.removeAttribute('data-checked');
      const optionImage = option.querySelector('img');
      if (optionImage && this.state.notSelectedImage) {
        optionImage.src = this.state.notSelectedImage;
      }
    }

    isOptionChecked(option) {
      return option.hasAttribute('data-checked');
    }

    handleMinusClick(product) {
      const productId = product.dataset.id;

      // Remove one instance of the product from state
      this.removeOneProductInstance(productId);

      // Update quantity display
      this.updateProductQuantity(product, productId);

      // If no more instances, reset visual state
      if (!this.hasProductInState(productId)) {
        this.resetProductCardVisualState(product);
      } else {
        // Product still in state, ensure it's marked as checked and has correct image
        product.setAttribute('data-checked', '');
        this.updateProductImageToProduct(product);
      }

      this.renderProductsToOptions();
    }

    updateProductImageToProduct(productElement) {
      const productImage = productElement.querySelector('.new-configurator__image');
      const productImageUrl = productElement.dataset.image;
      if (productImage && productImageUrl) {
        productImage.src = productImageUrl;
      }
    }

    removeOneProductInstance(productId) {
      // Try to remove from optional products first (last added)
      for (let i = this.state.optionalProducts.length - 1; i >= 0; i--) {
        if (this.state.optionalProducts[i]?.id === productId) {
          this.state.optionalProducts.splice(i, 1);
          this.recomputeDerivedState();
          return;
        }
      }

      // Otherwise remove from bowl products (last occurrence)
      for (let i = this.state.bowlProducts.length - 1; i >= 0; i--) {
        if (this.state.bowlProducts[i]?.id === productId) {
          this.state.bowlProducts[i] = null;
          // Compact to fill the gap
          this.compactBowlProducts();
          this.recomputeDerivedState();
          return;
        }
      }
    }

    handlePlusClick(product) {
      if (this.getFilledCount() >= this.state.maxQuantity) {
        return;
      }

      product.setAttribute('data-checked', '');
      this.updateProductImageToProduct(product);
      this.addProductToState(product);
      this.updateProductQuantity(product, product.dataset.id);
      this.renderProductsToOptions();
    }

    removeProductFromState(optionIndex) {
      if (this.isBowlOption(optionIndex)) {
        this.removeBowlProduct(optionIndex);
      } else {
        this.removeOptionalProduct(optionIndex);
      }

      this.recomputeDerivedState();
    }

    compactBowlProducts() {
      // Keep compacting until no more gaps can be filled
      let changed = true;
      while (changed) {
        changed = false;

        // Compact bowl products by shifting non-null values forward
        const compactedBowls = [];
        for (let i = 0; i < this.state.bowlProducts.length; i++) {
          if (this.state.bowlProducts[i] !== null) {
            compactedBowls.push(this.state.bowlProducts[i]);
          }
        }

        // Fill remaining slots with nulls
        while (compactedBowls.length < this.state.bowlProducts.length) {
          compactedBowls.push(null);
        }

        // If there's still a gap and we have optional products, move the first optional to fill it
        const firstNullIndex = compactedBowls.findIndex(p => p === null);
        if (firstNullIndex !== -1 && this.state.optionalProducts.length > 0) {
          const firstOptional = this.state.optionalProducts.shift();
          compactedBowls[firstNullIndex] = firstOptional;
          this.state.bowlProducts = compactedBowls;
          changed = true; // Continue loop to check for more gaps
        } else {
          this.state.bowlProducts = compactedBowls;
          break; // No more changes possible
        }
      }
    }

    removeBowlProduct(optionIndex) {
      if (Number.isInteger(optionIndex) && optionIndex < this.state.bowlProducts.length) {
        // Remove the bowl product at the given index
        this.state.bowlProducts[optionIndex] = null;
        // Compact to fill the gap
        this.compactBowlProducts();
      }
    }

    removeOptionalProduct(optionIndex) {
      const optionalIdx = this.convertToOptionalIndex(optionIndex);

      if (optionalIdx !== undefined && optionalIdx < this.state.optionalProducts.length) {
        this.state.optionalProducts.splice(optionalIdx, 1);
      }
    }

    convertToOptionalIndex(optionIndex) {
      if (Number.isInteger(optionIndex) && optionIndex >= this.state.bowlsQuantity) {
        return optionIndex - this.state.bowlsQuantity;
      }
      return undefined;
    }

    addProductToState(product) {
      const optionType = this.getOptionTypeFromProduct(product);
      const productData = this.extractProductData(product, optionType);

      if (this.isBowlOption(this.state.activeOption)) {
        this.addBowlProduct(productData);
      } else {
        this.addOptionalProduct(productData);
      }

      this.recomputeDerivedState();
    }

    extractProductData(product, optionType) {
      // Get the variant ID, price, and compare-at-price from the active option element
      const activeOptionIndex = this.state.activeOption;
      const activeOptionElement = this.options?.[activeOptionIndex];
      const variantId = activeOptionElement?.dataset?.variantId || null;
      const price = activeOptionElement?.getAttribute('data-price') || null;
      const compareAtPrice = activeOptionElement?.getAttribute('data-compare-at-price') || null;
      // Ensure additionalPrice is always a boolean
      const additionalPrice = product.hasAttribute('data-additional-price') === true;

      return {
        id: product.dataset.id || product.dataset.sku, // Use data-id if available, fallback to sku
        title: product.dataset.title,
        image: product.dataset.image,
        sku: product.dataset.sku,
        type: optionType,
        variantId: variantId,
        price: price,
        compareAtPrice: compareAtPrice,
        additionalPrice: additionalPrice, // Always boolean: true or false
      };
    }

    addBowlProduct(productData) {
      const slotIndex = this.state.bowlProducts.findIndex((p) => p === null);
      if (slotIndex === -1) return;
      this.state.bowlProducts[slotIndex] = productData;
    }

    addOptionalProduct(productData) {
      if (this.state.optionalProducts.length >= this.state.optionalQuantity) return;
      this.state.optionalProducts.push(productData);
    }

    getProductCount(productId) {
      const bowls = this.state.bowlProducts.filter(p => p && p.id === productId).length;
      const optionals = this.state.optionalProducts.filter(p => p.id === productId).length;
      return bowls + optionals;
    }

    hasProductInState(productId) {
      return this.getProductCount(productId) > 0;
    }

    updateProductQuantity(product, productId) {
      const count = this.getProductCount(productId);
      const quantityInput = product.querySelector('[data-quantity]');
      if (quantityInput) {
        quantityInput.value = Math.max(0, count);
      }
      if (count <= 0) {
        product.removeAttribute('data-checked');
      }
    }

    updateOptionBorders() {
      if (!this.options) return;

      this.state.activeOption = this.clampActiveOption(this.state.activeOption);
      this.clearAllOptionBorders();
      this.setActiveOptionBorder();
    }

    clearAllOptionBorders() {
      this.options.forEach((option) => {
        option.removeAttribute('data-border');
      });
    }

    setActiveOptionBorder() {
      if (this.state.activeOption >= 0 && this.state.activeOption < this.options.length) {
        const isLastOption = this.state.activeOption === this.options.length - 1;
        const isChecked = this.options[this.state.activeOption].hasAttribute('data-checked');

        // Don't set border if it's the last option and it's checked
        if (isLastOption && isChecked) {
          return;
        }

        this.options[this.state.activeOption].setAttribute('data-border', '');
      }
    }

    ensureOptionImage(option) {
      return option.querySelector('img');
    }

    resetOptionElement(option, fallbackImage) {
      option.removeAttribute('data-checked');
      option.removeAttribute('data-border');
      const optionImage = option.querySelector('img');
      if (optionImage && fallbackImage) {
        optionImage.src = fallbackImage;
      }
    }

    renderOptionElement(option, product, fallbackImage) {
      if (!product) return;
      const image = this.ensureOptionImage(option);

      if (!image) return;

      if (product.image) {
        image.src = product.image;
        image.alt = product.title || '';
        image.removeAttribute('srcset');
      } else if (fallbackImage) {
        image.src = fallbackImage;
      }

      option.setAttribute('data-checked', '');
    }

    renderOptionList(options, renderList, fallbackImage) {
      if (!options) return;
      options.forEach((option) => this.resetOptionElement(option, fallbackImage));
      renderList.forEach((product, index) => {
        if (index < options.length) {
          this.renderOptionElement(options[index], product, fallbackImage);
        }
      });
    }

    renderProductsToOptions() {
      if (!this.options) return;

      const renderList = this.state.optionsRender || [];
      this.renderOptionList(this.options, renderList, this.state.notSelectedImage);

      // Update option borders after rendering
      this.updateOptionBorders();

      // Update new-configurator options as well
      if (this.newConfigurator) {
        this.newConfigurator.updateOptions(this.state);
      }

      // Update selected bowls display
      this.updateSelectedBowlsDisplay();

      // Update prices
      this.updatePrices(this.state);

      // Update button states
      this.updateButtonStates();

      // Reinitialize slider after render
      this.configuratorSlider?.reinitialize();
    }

    setActiveFilter(activeFilter) {
      this.filters.forEach((filter) => filter.removeAttribute('data-active'));
      activeFilter.setAttribute('data-active', '');
      const filterValue = activeFilter.getAttribute('data-filter');
      this.state.activeFilter = filterValue;
    }

    updateTabs(filterValue) {
      this.tabs.forEach((tab) => {
        const tabValue = tab.getAttribute('data-tab');
        tab.removeAttribute('data-open');

        if (tabValue === filterValue) {
          tab.setAttribute('data-open', '');
        }
      });
    }

    handleOutsideClick(event) {
      if (!this.hasAttribute('data-open')) return;
      // Only close if click is completely outside the sidebar element
      // Backdrop clicks are handled by setupBackdropClickListener
      if (!this.contains(event.target)) {
        this.closeSidebar();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleOutsideClick);
    }

    openSidebar() {
      this.style.zIndex = '1999';
      this.setAttribute('data-open', '');

      // Calculate scrollbar width to prevent page shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      // Hide overflow and add padding to compensate for scrollbar
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    closeSidebar() {
      this.removeAttribute('data-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';

      // Keep z-index high during closing animation, then reset after animation completes
      setTimeout(() => {
        if (!this.hasAttribute('data-open')) {
          this.style.zIndex = '-1999';
        }
      }, 300);
    }

    updateSelectedBowlsDisplay() {
      const selectedBowlsElement = document.querySelector('[data-selected-bowls]');
      const selectedTotalElement = document.querySelector('[data-selected-total]');

      if (!selectedBowlsElement || !selectedTotalElement) return;

      const filledCount = this.getFilledCount();
      const bowlsQuantity = this.state.bowlsQuantity;

      // Show only the count (numerator) in data-selected-bowls
      selectedBowlsElement.textContent = filledCount;

      const totalQuantity = filledCount <= bowlsQuantity ? bowlsQuantity : filledCount;
      const format =
        (selectedTotalElement.dataset.quantityTextFormat || "").trim() ||
        "von %quantity% ausgewählt";
      selectedTotalElement.textContent = format.replaceAll(
        "%quantity%",
        String(totalQuantity)
      );
    }


    formatMoney(price) {
      return "€" + (price / 100).toFixed(2).replace(".", ",").replace(",00", "");
    }

    getActiveOptionPrice(state) {
      // Get price from currentVariant in state
      const currentVariant = state?.currentVariant;

      if (currentVariant) {
        return {
          price: currentVariant.price || 0,
          compareAtPrice: currentVariant.compareAtPrice || 0
        };
      }

      // Fallback: When currentVariant is not available, get price from first variant (first option element)
      if (this.options && this.options.length > 0) {
        const firstOption = this.options[0];
        const price = firstOption.getAttribute('data-price');
        const compareAtPrice = firstOption.getAttribute('data-compare-at-price');

        return {
          price: price ? parseInt(price) : 0,
          compareAtPrice: compareAtPrice ? parseInt(compareAtPrice) : 0
        };
      }
      return { price: 0, compareAtPrice: 0 };
    }

    updatePrices(state) {
      if (!this.prices || !this.comparePrices) return;

      const { price, compareAtPrice } = this.getActiveOptionPrice(state);

      // Update regular prices
      this.prices.forEach(priceElement => {
        priceElement.textContent = this.formatMoney(price);
        priceElement.setAttribute('data-final-price', String(price));
      });

      // Check if compare_at_price exists and is greater than price
      const hasComparePrice = compareAtPrice && compareAtPrice > price;

      this.comparePrices.forEach(comparePriceElement => {
        if (hasComparePrice) {
          comparePriceElement.textContent = this.formatMoney(compareAtPrice);
          comparePriceElement.style.display = '';
        } else {
          comparePriceElement.style.display = 'none';
        }
      });

      document.dispatchEvent(
        new CustomEvent('new-configurator:price-updated', {
          detail: { price, compareAtPrice }
        })
      );
    }

    setupSidebarButtonListener() {
      if (this.sidebarButton) {
        this.sidebarButton.addEventListener('click', () => {
          this.closeSidebar();
        });
      }
    }

    async render() {
      const newHTML = await this.fetchSection();
      const parser = new DOMParser();
      let resAsHtml = parser.parseFromString(newHTML, 'text/html');
      const updatedCart = resAsHtml.querySelector('.cart-drawer');
      const currentContent = document.querySelector('.drawer__inner');
      const newContent = updatedCart.querySelector('.drawer__inner');
      document.querySelector('cart-drawer').classList.remove('is-empty');
      if (currentContent.innerHTML === newContent.innerHTML) return;
      currentContent.innerHTML = newContent.innerHTML;
    }

    async fetchSection() {
      const res = await fetch(
        window.Shopify.routes.root + '?section_id=cart-drawer'
      );
      const text = await res.text();
      return text;
    }

    async addToDrawer() {
      const mainItem = this.getMainItemForCart();
      if (!mainItem) {
        console.error('No variant selected');
        return;
      }

      const formData = {
        items: [mainItem]
      };

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });

        const data = await response.json();
        await this.render();
        await this.cartDrawer.open();
        this.closeSidebar();
        console.log(data);
      } catch (error) {
        console.error('Error:', error);
      }
    }

    getMainItemForCart() {
      const currentVariant = this.state?.currentVariant;
      const optionsRender = this.state?.optionsRender || [];

      if (!currentVariant || !currentVariant.id) {
        return null;
      }

      // Collect all titles and skus from non-null items
      const titles = [];
      const skues = [];
      optionsRender.forEach(item => {
        if (item !== null && item !== undefined) {
          if (item.title) titles.push(item.title);
          if (item.sku) skues.push(item.sku);
        }
      });

      return {
        id: currentVariant.id,
        quantity: 1,
        properties: {
          "Varianten": titles.join('; '),
          "_Artikelnummern": skues.join(',')
        }
      };
    }

    areAllBowlsFilled() {
      return this.state.bowlProducts.every(product => product !== null && product !== undefined);
    }

    disabledButtons() {
      if (this.addButton) {
        this.addButton.setAttribute('aria-disabled', 'true');
        if (this.addButton.parentElement) {
          this.addButton.parentElement.setAttribute('aria-disabled', 'true');
        }
      }
      // Also update the button in NewConfigurator
      if (this.newConfigurator && this.newConfigurator.addButton) {
        this.newConfigurator.addButton.setAttribute('aria-disabled', 'true');
        if (this.newConfigurator.addButton.parentElement) {
          this.newConfigurator.addButton.parentElement.setAttribute('aria-disabled', 'true');
        }
      }
    }

    enabledButtons() {
      if (this.addButton) {
        this.addButton.setAttribute('aria-disabled', 'false');
        if (this.addButton.parentElement) {
          this.addButton.parentElement.setAttribute('aria-disabled', 'false');
        }
      }
      // Also update the button in NewConfigurator
      if (this.newConfigurator && this.newConfigurator.addButton) {
        this.newConfigurator.addButton.setAttribute('aria-disabled', 'false');
        if (this.newConfigurator.addButton.parentElement) {
          this.newConfigurator.addButton.parentElement.setAttribute('aria-disabled', 'false');
        }
      }
    }

    updateButtonStates() {
      if (this.areAllBowlsFilled()) {
        this.enabledButtons();
        if (this.sidebarButton) {
          this.sidebarButton.setAttribute('data-active', '');
        }
      } else {
        this.disabledButtons();
        if (this.sidebarButton) {
          this.sidebarButton.removeAttribute('data-active');
        }
      }
    }
  }
  customElements.define('configurator-sidebar', ConfiguratorSidebar);
}


if (!customElements.get('configurator-slider')) {
  class ConfiguratorSlider extends HTMLElement {
    constructor() {
      super();

      // Slider elements
      this.sliderList = this.querySelector('[data-list]');
      this.left = this.querySelector('[data-left]');
      this.right = this.querySelector('[data-right]');

      // Initial state
      this.slide = this.sliderList?.children[0] || null;
      this.currentIndex = 0;
      this.isMouseDown = false;
      this.startX = 0;
      this.dragged = false; // Track whether dragging occurred

      this.setupEventListeners();

      this.setupObservers();

      this.visibilityNavigation();

      this.scrollToBorderedSlide();
    }

    setupEventListeners() {
      this.left?.addEventListener('click', () => this.scrollLeft());
      this.right?.addEventListener('click', () => this.scrollRight());

      // Add scroll listener to detect when scrolled to the end
      this.sliderList?.addEventListener('scroll', () => this.handleScroll());

      if (window.innerWidth >= 750) {
        // Mouse drag support
        this.sliderList.addEventListener('mousedown', this.onMouseDown.bind(this));
        this.sliderList.addEventListener('mousemove', this.onMouseMove.bind(this));
        this.sliderList.addEventListener('mouseup', this.onMouseUp.bind(this));
        this.sliderList.addEventListener('mouseleave', this.onMouseUp.bind(this));

        // Prevent link navigation on drag
        this.sliderList.querySelectorAll('a').forEach((link) => {
          link.addEventListener('click', (event) => {
            if (this.dragged) {
              event.preventDefault();
            }
          });
        });

        // Prevent play video on drag
        this.sliderList.querySelectorAll('custom-video').forEach((video) => {
          video.addEventListener('click', (event) => {
            if (this.dragged) {
              event.stopImmediatePropagation();
            }
          });
        });
      }
    }

    handleScroll() {
      if (!this.sliderList) return;

      const scrollLeft = this.sliderList.scrollLeft;
      const scrollWidth = this.sliderList.scrollWidth;
      const clientWidth = this.sliderList.clientWidth;
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1; // -1 for rounding errors
      const isAtStart = scrollLeft <= 1; // 1 for rounding errors

      // Ensure proper alignment at start and end
      if (isAtStart) {
        // Snap to start: first slide aligned with list start
        this.sliderList.scrollLeft = 0;
        this.currentIndex = 0;
      } else if (isAtEnd) {
        // Snap to end: last slide aligned with list end
        const maxScroll = scrollWidth - clientWidth;
        this.sliderList.scrollLeft = maxScroll;
        const size = this.sliderList.children.length;
        const slidesPerView = this.getSlidesPerView();
        this.currentIndex = Math.max(0, size - slidesPerView);
      }

      // Update navigation visibility based on scroll position
      this.visibilityNavigation();
    }

    onMouseDown(event) {
      this.isMouseDown = true;
      this.startX = event.pageX - this.sliderList.offsetLeft;
      this.dragged = false; // Reset dragged state on new drag
      this.sliderList.style.cursor = 'grabbing'; // Change cursor to indicate dragging
    }

    onMouseMove(event) {
      if (!this.isMouseDown) return;
      const moveX = event.pageX - this.sliderList.offsetLeft;
      const distance = moveX - this.startX;

      // If movement is significant, set dragged to true
      if (Math.abs(distance) > 5) {
        this.dragged = true;
      }

      if (distance < 0) {
        this.scrollRight();
      } else {
        this.scrollLeft();
      }
    }

    onMouseUp() {
      this.isMouseDown = false;
      this.sliderList.style.cursor = 'grab'; // Reset cursor
    }

    scrollLeft() {
      if (!this.sliderList || !this.slide) return;
      const newScrollLeft = this.sliderList.scrollLeft - this.slide.clientWidth;
      this.sliderList.scrollLeft = Math.max(0, newScrollLeft);
      // Ensure alignment at start
      if (this.sliderList.scrollLeft <= 1) {
        this.sliderList.scrollLeft = 0;
      }
    }

    scrollRight() {
      if (!this.sliderList || !this.slide) return;
      const scrollWidth = this.sliderList.scrollWidth;
      const clientWidth = this.sliderList.clientWidth;
      const maxScroll = scrollWidth - clientWidth;
      const newScrollLeft = this.sliderList.scrollLeft + this.slide.clientWidth;
      this.sliderList.scrollLeft = Math.min(maxScroll, newScrollLeft);
      // Ensure alignment at end
      if (this.sliderList.scrollLeft + clientWidth >= scrollWidth - 1) {
        this.sliderList.scrollLeft = maxScroll;
      }
    }

    setupObservers() {
      // Store observer reference for later updates
      if (this.sliderScrollObserver) {
        this.sliderScrollObserver.disconnect();
      }

      // Calculate the rootMargin to only show the current slide based on slidesPerView
      let slidesPerView = this.getSlidesPerView();

      let rootMargin = "0px 0px 0px 0px";
      if (slidesPerView > 1) {
        // Calculate the percentage to hide based on slides per view
        // For 3 slides per view, we want to hide 66.66% from the right
        const hidePercentage = ((slidesPerView - 1) / slidesPerView) * 100;
        rootMargin = `0px -${hidePercentage}% 0px 0px`;
      }
      this.sliderScrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              slidesPerView = this.getSlidesPerView();
              this.currentIndex = parseInt(entry.target.dataset.index, 10);
              this.visibilityNavigation();
            }
          });
        },
        {
          root: this.sliderList,
          rootMargin: rootMargin,
          threshold: 0.3
        }
      );
      // Assign `data-index` to each slide for identification
      [...this.sliderList.children].forEach((slide, index) => {
        slide.dataset.index = index;
        this.sliderScrollObserver.observe(slide);
      });
    }

    visibilityNavigation() {
      if (!this.sliderList) return;

      // Check for overflow first
      this.showSliderNavigationIfOverflowing();

      const scrollLeft = this.sliderList.scrollLeft;
      const scrollWidth = this.sliderList.scrollWidth;
      const clientWidth = this.sliderList.clientWidth;
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1; // -1 for rounding errors
      const isAtStart = scrollLeft <= 1; // 1 for rounding errors

      // If all slides are visible at once, hide all buttons
      const totalSlides = this.sliderList.children.length;
      const slidesPerView = this.getSlidesPerView();
      if (totalSlides <= slidesPerView) {
        this.left?.setAttribute('data-active', 'false');
        this.right?.setAttribute('data-active', 'false');
        return;
      }

      // When at start: only right button visible
      if (isAtStart) {
        this.left?.setAttribute('data-active', 'false');
        this.right?.setAttribute('data-active', 'true');
      }
      // When at end: only left button visible
      else if (isAtEnd) {
        this.left?.setAttribute('data-active', 'true');
        this.right?.setAttribute('data-active', 'false');
      }
      // When not at start or end: both buttons visible
      else {
        this.left?.setAttribute('data-active', 'true');
        this.right?.setAttribute('data-active', 'true');
      }
    }

    showSliderNavigationIfOverflowing() {
      if (!this.sliderList) return;

      const hasOverflow = this.sliderList.scrollWidth > this.sliderList.clientWidth;
      if (hasOverflow) {
        this.left?.removeAttribute('data-hide');
        this.right?.removeAttribute('data-hide');
      } else {
        this.left?.setAttribute('data-hide', '');
        this.right?.setAttribute('data-hide', '');
      }
    }

    reinitialize() {
      // Wait for DOM updates before reinitializing
      requestAnimationFrame(() => {
        // Reinitialize observers when content changes
        if (this.sliderScrollObserver) {
          this.sliderScrollObserver.disconnect();
        }

        // Reset current index
        this.currentIndex = 0;
        this.slide = this.sliderList?.children[0] || null;

        // Re-setup observers
        this.setupObservers();

        // Update visibility
        this.visibilityNavigation();

        // Scroll to bordered slide if it exists
        this.scrollToBorderedSlide();
      });
    }

    getSlidesPerView() {
      const sliderContainer = this.sliderList;
      const hasOneButton = this.hasAttribute('data-one-button');

      // When data-one-button is present (configurator), only count checked slides
      let slides = [...this.sliderList.children];
      if (hasOneButton) {
        slides = slides.filter(slide => slide.hasAttribute('data-checked'));
      }

      if (!sliderContainer || slides.length === 0) return 1;

      const containerWidth = sliderContainer.clientWidth;
      const slideWidth = slides[0].clientWidth;

      return Math.max(1, Math.floor(containerWidth / slideWidth));
    }

    scrollToBorderedSlide() {
      if (!this.sliderList || !this.slide) return;

      // Find the slide with data-border attribute
      const borderedSlide = Array.from(this.sliderList.children).find(
        (slide) => slide.hasAttribute('data-border')
      );

      if (borderedSlide) {
        // Calculate the scroll position to center the bordered slide in the middle of the list
        const slideIndex = Array.from(this.sliderList.children).indexOf(borderedSlide);
        const slideWidth = this.slide.clientWidth;
        const containerWidth = this.sliderList.clientWidth;
        const scrollWidth = this.sliderList.scrollWidth;

        // Calculate the position of the bordered slide
        const slidePosition = slideWidth * slideIndex;

        // Calculate scroll position to center the slide: slide center - container center
        const slideCenter = slidePosition + (slideWidth / 2);
        const containerCenter = containerWidth / 2;
        let scrollPosition = slideCenter - containerCenter;

        // Ensure we don't scroll beyond bounds
        const maxScroll = scrollWidth - containerWidth;
        scrollPosition = Math.max(0, Math.min(scrollPosition, maxScroll));

        // Scroll to center the bordered slide
        this.sliderList.scrollLeft = scrollPosition;

        // Update current index
        this.currentIndex = slideIndex;

        // Update visibility after scrolling
        requestAnimationFrame(() => {
          this.visibilityNavigation();
        });
      }
    }
  }

  customElements.define('configurator-slider', ConfiguratorSlider);
}
